import React from 'react';
import {
    Box,
    Chip,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import useTranslate from 'hooks/useTranslate';
import type { ProviderActivity } from 'hooks/analytics';

import ProviderActivityHeatmap from './ProviderActivityHeatmap';
import {
    AnalyticsColors,
    SectionCard,
    SectionSubtitle,
    SectionTitle,
    tdSx,
    thSx,
    trHoverSx,
} from '../../../components/analyticsUi';

interface ProviderActivityTableProps {
    providers: ProviderActivity[];
    selectedProviderId: number | null;
    onProviderSelect: (providerId: number) => void;
}

const getTodayKey = (): string => new Date().toISOString().slice(0, 10);

const formatDay = (day: string): string =>
    new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(new Date(`${day}T00:00:00`));

const formatDateTime = (value: string | null): string => {
    if (!value) return '—';

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
};

const DailyBreakdown = ({ provider }: { provider: ProviderActivity }) => {
    const { translate } = useTranslate();
    const todayKey = getTodayKey();

    return (
        <Box sx={{ py: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.25 }}>
                {provider.name} — {translate('dailyBreakdown')}
            </Typography>
            <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader aria-label={translate('dailyBreakdown')}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={thSx()}>{translate('day')}</TableCell>
                            <TableCell sx={thSx({ align: 'right' })}>
                                {translate('recordedActions')}
                            </TableCell>
                            <TableCell sx={thSx({ align: 'right' })}>
                                {translate('rawEventVolume')}
                            </TableCell>
                            <TableCell sx={thSx({ align: 'right' })}>
                                {translate('activeClients')}
                            </TableCell>
                            <TableCell sx={thSx({ align: 'right' })}>
                                {translate('apiActions')}
                            </TableCell>
                            <TableCell sx={thSx({ align: 'right' })}>
                                {translate('automatedEvents')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {provider.daily.map((metric) => {
                            const isToday = metric.day === todayKey;

                            return (
                                <TableRow key={metric.day}>
                                    <TableCell sx={tdSx({ bold: isToday })}>
                                        {formatDay(metric.day)}
                                        {isToday ? (
                                            <Chip
                                                label={translate('livePartialDay')}
                                                size="small"
                                                sx={{
                                                    ml: 0.75,
                                                    height: 20,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    color: AnalyticsColors.primary,
                                                    backgroundColor: AnalyticsColors.primaryLight,
                                                }}
                                            />
                                        ) : null}
                                    </TableCell>
                                    <TableCell sx={tdSx({ align: 'right', bold: isToday })}>
                                        {metric.recordedActions.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={tdSx({ align: 'right' })}>
                                        {metric.recordedEvents.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={tdSx({ align: 'right' })}>
                                        {metric.activeClients.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={tdSx({ align: 'right' })}>
                                        {metric.apiActions.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={tdSx({ align: 'right' })}>
                                        {metric.automatedEvents.toLocaleString()}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const ProviderActivityTable: React.FC<ProviderActivityTableProps> = ({
    providers,
    selectedProviderId,
    onProviderSelect,
}) => {
    const { translate } = useTranslate();

    return (
        <SectionCard sx={{ p: { xs: 1, sm: 1.5 } }}>
            <SectionTitle>{translate('providerActivityByProvider')}</SectionTitle>
            <SectionSubtitle>{translate('providerActivityTableHint')}</SectionSubtitle>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label={translate('providerActivity')}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={thSx()}>{translate('provider')}</TableCell>
                            <TableCell sx={thSx({ align: 'right' })}>
                                {translate('totalRecordedActions')}
                            </TableCell>
                            <TableCell sx={thSx({ align: 'right' })}>
                                {translate('activeStaff')}
                            </TableCell>
                            <TableCell sx={thSx()}>{translate('lastActivity')}</TableCell>
                            <TableCell sx={thSx()}>{translate('dailyRecordedActivity')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {providers.map((provider) => {
                            const isSelected = selectedProviderId === provider.id;

                            return (
                                <React.Fragment key={provider.id}>
                                    <TableRow
                                        hover
                                        selected={isSelected}
                                        tabIndex={0}
                                        aria-expanded={isSelected}
                                        onClick={() => onProviderSelect(provider.id)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                onProviderSelect(provider.id);
                                            }
                                        }}
                                        sx={[
                                            trHoverSx,
                                            {
                                                cursor: 'pointer',
                                                '&:focus-visible': {
                                                    outline: `2px solid ${AnalyticsColors.primary}`,
                                                    outlineOffset: -2,
                                                },
                                            },
                                        ]}
                                    >
                                        <TableCell sx={tdSx({ bold: true })}>
                                            {provider.name}
                                        </TableCell>
                                        <TableCell sx={tdSx({ align: 'right', bold: true })}>
                                            {provider.totals.recordedActions.toLocaleString()}
                                        </TableCell>
                                        <TableCell sx={tdSx({ align: 'right' })}>
                                            {provider.totals.activeStaff.toLocaleString()}
                                        </TableCell>
                                        <TableCell sx={tdSx()}>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: 13,
                                                    color: provider.lastActivityAt
                                                        ? 'text.primary'
                                                        : 'text.secondary',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {formatDateTime(provider.lastActivityAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={tdSx()}>
                                            <Box sx={{ overflowX: 'auto', maxWidth: 540 }}>
                                                <ProviderActivityHeatmap
                                                    daily={provider.daily}
                                                    compact
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            sx={{
                                                p: 0,
                                                borderBottom: isSelected ? undefined : 'none',
                                            }}
                                        >
                                            <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                                <DailyBreakdown provider={provider} />
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </SectionCard>
    );
};

export default ProviderActivityTable;
